import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaLock, FaClock, FaBan, FaShieldAlt, FaRocket, FaCheck, FaArrowRight, FaCrown, FaUsers, FaEnvelope } from 'react-icons/fa';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';

const accentBlue = "#2186EB";
const privacyOrange = "#FF7417";

function LandingPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: <FaLock className="text-4xl text-[#2186EB]" aria-hidden="true" />,
      title: "End-to-End Encryption",
      desc: "Your files are encrypted with AES-256 before they leave your browser. Only you and your recipient can decrypt."
    },
    {
      icon: <FaClock className="text-4xl text-[#2186EB]" aria-hidden="true" />,
      title: "Self-Destruct & Expiry",
      desc: "Set files to auto-delete after a set time or number of downloads. Your data, your rules."
    },
    {
      icon: <FaBan className="text-4xl text-[#2186EB]" aria-hidden="true" />,
      title: "Zero Knowledge",
      desc: "We never see, log, or analyze your files. True privacy by design."
    },
    {
      icon: <FaShieldAlt className="text-4xl text-[#2186EB]" aria-hidden="true" />,
      title: "Password Protection",
      desc: "Add an extra layer of security with password-protected downloads using SRP protocol."
    }
  ];

  const plans = [
    {
      name: 'Free',
      price: '€0',
      period: 'forever',
      features: ['10 shares/month', '30MB max file', '24h expiry max', '5 downloads/file', '1 intake portal'],
      cta: 'Get Started',
      icon: <FaLock />
    },
    {
      name: 'Pro',
      price: '€11',
      period: '/month',
      features: ['Unlimited shares', '2GB max file', '30 day expiry', '100 downloads/file', 'Unlimited portals'],
      cta: 'Upgrade to Pro',
      popular: true,
      icon: <FaCrown />
    },
    {
      name: 'Team',
      price: '€7',
      period: '/user/month',
      features: ['Everything in Pro', '5GB max file', '90 day expiry', '1000 downloads/file', 'Team management'],
      cta: 'Upgrade to Team',
      icon: <FaUsers />
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Secure Encrypted File Sharing | Self-Destructing Links"
        description="Share files securely with end-to-end AES-256 encryption. Zero-knowledge architecture means we never see your files. Self-destructing links, password protection, QR code sharing. Free."
        url="https://miragedocs.com"
      />
      {/* Header */}
      <header className="w-full py-6 px-6 bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-gray-900 flex items-center gap-2" aria-label="MirageDocs home">
            <FaLock className="text-[#2186EB]" aria-hidden="true" />
            MirageDocs
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-gray-600 hover:text-gray-900 transition">Features</a>
            <a href="#pricing" className="text-gray-600 hover:text-gray-900 transition">Pricing</a>
            <a href="#contact" className="text-gray-600 hover:text-gray-900 transition">Contact</a>
            <Link to="/login" className="text-gray-600 hover:text-gray-900 transition">Sign In</Link>
            <Link
              to="/register"
              className="px-5 py-2.5 bg-[#FF7417] text-white rounded-lg font-semibold hover:bg-orange-600 transition"
            >
              Sign Up Free
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-block px-4 py-2 bg-blue-50 text-[#2186EB] rounded-full text-sm font-medium mb-6">
            Privacy-first file sharing
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Share Files Securely.<br />
            <span className="text-[#2186EB]">Zero Knowledge.</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-10">
            End-to-end encrypted file sharing with self-destructing links.
            Your files are encrypted before upload — we never see them.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/register')}
              className="px-8 py-4 bg-[#FF7417] text-white rounded-xl font-bold text-lg hover:bg-orange-600 transition flex items-center justify-center gap-2"
            >
              Start Sharing Free <FaArrowRight />
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-8 py-4 bg-gray-100 text-gray-900 rounded-xl font-bold text-lg hover:bg-gray-200 transition"
            >
              Sign In
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-6">
            No credit card required. Free forever for personal use.
          </p>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 bg-gray-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 text-gray-400">
            <div className="flex items-center gap-2">
              <FaShieldAlt className="text-2xl" aria-hidden="true" />
              <span className="font-medium">AES-256 Encrypted</span>
            </div>
            <div className="flex items-center gap-2">
              <FaLock className="text-2xl" aria-hidden="true" />
              <span className="font-medium">Zero Knowledge</span>
            </div>
            <div className="flex items-center gap-2">
              <FaRocket className="text-2xl" aria-hidden="true" />
              <span className="font-medium">GDPR Compliant</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Security Without Compromise
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Built from the ground up with privacy as the foundation, not an afterthought.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, idx) => (
              <div key={idx} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition">
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">Simple, secure, straightforward.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#2186EB] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">1</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Upload & Encrypt</h3>
              <p className="text-gray-600">Select your file. It's encrypted in your browser before upload.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#2186EB] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">2</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Set Rules</h3>
              <p className="text-gray-600">Choose expiry time, download limits, and add optional password.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#2186EB] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">3</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Share Securely</h3>
              <p className="text-gray-600">Get a secure link or QR code. File self-destructs after rules are met.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600">Start free. Upgrade when you need more.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, idx) => (
              <div
                key={idx}
                className={`bg-white rounded-2xl p-8 border-2 ${
                  plan.popular ? 'border-[#2186EB] shadow-xl scale-105' : 'border-gray-100'
                } relative`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#2186EB] text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="text-center mb-6">
                  <div className="text-[#2186EB] text-2xl mb-3">{plan.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-600">{plan.period}</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-center gap-2">
                      <FaCheck className="text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/register')}
                  className={`w-full py-3 rounded-lg font-semibold transition ${
                    plan.popular
                      ? 'bg-[#2186EB] text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/pricing" className="text-[#2186EB] hover:underline font-medium">
              View full pricing details <FaArrowRight className="inline ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#2186EB] text-white rounded-full mb-6">
              <FaEnvelope className="text-2xl" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Get in Touch
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Have a question, found a bug, or want to request a feature? We'd love to hear from you.
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <ContactForm />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#2186EB]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Share Securely?
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Join thousands who trust MirageDocs for private file sharing.
          </p>
          <button
            onClick={() => navigate('/register')}
            className="px-10 py-4 bg-white text-[#2186EB] rounded-xl font-bold text-lg hover:bg-gray-100 transition"
          >
            Create Free Account
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-gray-900 text-gray-400">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2 text-white">
              <FaLock aria-hidden="true" />
              <span className="font-bold">MirageDocs</span>
            </div>
            <div className="flex flex-wrap justify-center gap-6 md:gap-8">
              <Link to="/pricing" className="hover:text-white transition">Pricing</Link>
              <a href="#features" className="hover:text-white transition">Features</a>
              <a href="#contact" className="hover:text-white transition">Contact</a>
              <Link to="/security" className="hover:text-white transition">Security</Link>
              <Link to="/privacy" className="hover:text-white transition">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition">Terms</Link>
              <Link to="/login" className="hover:text-white transition">Sign In</Link>
            </div>
            <div className="text-sm">
              Privacy First. Zero Logs.
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-gray-800 text-center text-sm">
            <p>End-to-end encrypted file sharing. Your files, your control.</p>
            <p className="mt-2 text-gray-500">Luxembourg</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
